import flatten from 'lodash/flatten';

import { ACCOUNT_CODES, ACCOUNT_CATEGORY_IDS } from './constants';
import CategoryHeader from './CategoryHeader';
import type {
  DataEntry,
  DateRange,
  GeneralLedgerListItem,
  LedgerAccount,
  LedgerAccountCategory,
  TaskStatusFilter,
} from './types';
import DataEntryHeaderRow from './DataEntryHeaderRow';
import DataEntryRow from './DataEntryRow';
import formattedCentsAmount from './formattedCentsAmount';
import LedgerAccountHeader from './LedgerAccountHeader';
import LedgerNavigationHeader from './LedgerNavigationHeader';
import LedgerNavigationLink from './LedgerNavigationLink';
import NoDataMessage from './NoDataMessage';
import SpacerListItem from './SpacerListItem';
import { Client } from './types';
import { SplitFeatureFlags } from 'bk-web-app/features';
import { formatSourceInfo } from './helpers';
import { SortState } from './contexts';

type MakeGeneralLedgerListItemDataArgs = {
  ledgerAccountCategories: Array<LedgerAccountCategory>;
  dataEntries: Array<DataEntry>;
  dateRange: DateRange;
  selectedDataEntry?: DataEntry;
  search: string | typeof undefined;
  showAccounts: boolean;
  showQualityIssues: boolean;
  taskStatusFilter: TaskStatusFilter;
  hasQualityIssues: boolean;
  toggleDataEntry: (entry: DataEntry) => any;
  toggleLedgerAccount: (ledger: LedgerAccount) => any;
  client?: Client;
  moneyInTransitLedgerId?: string;
  sortStates: Record<string, SortState>;
  updateSortState: (ledgerAccountId: string, sortState: SortState) => void;
  filterMito: boolean;
  updateFilterMito: () => void;
  matchingEntryIds?: Set<string>;
  showSourceColumn: boolean;
  showTasksColumn?: boolean;
  potentialMatches?: PotentialMatchesResult;
};

export const isAccount = (ledgerAccount: LedgerAccount) => ledgerAccount && !!ledgerAccount.role;

// Cache column headers to avoid recreation on every render
const columnHeadersCache = new Map<string, Array<any>>();

// Cache for potential match header columns to avoid recreation
const potentialMatchHeaderCache = new Map<boolean, any>();

const getPotentialMatchHeaderColumn = (isMoneyInTransitOutstanding: boolean) => {
  if (potentialMatchHeaderCache.has(isMoneyInTransitOutstanding)) {
    return potentialMatchHeaderCache.get(isMoneyInTransitOutstanding)!;
  }

  const header = isMoneyInTransitOutstanding
    ? {
        heading: 'Toggle filtering for transactions with potential matches',
        icon: 'filter',
      }
    : {
        heading: '',
        width: '10px',
      };

  potentialMatchHeaderCache.set(isMoneyInTransitOutstanding, header);
  return header;
};

const getGeneralLedgerColumnHeaders = (showSourceColumn: boolean, showTasksColumn = false) => {
  const cacheKey = `${showSourceColumn}-${showTasksColumn}`;
  if (columnHeadersCache.has(cacheKey)) {
    return columnHeadersCache.get(cacheKey)!;
  }

  const headers = [
    { heading: 'Date', width: '70px', sortKey: 'journalEntry.dateOfTransaction' },
    {
      heading: 'Transaction Name',
      width: 'auto',
      minWidth: '120px',
      sortKey: 'journalEntry.vendorName',
    },
    { heading: ' ', width: '40px' },
    {
      heading: 'Contra Ledger',
      width: '150px',
      getColumnVisibility: isAccount,
    },
    ...(showSourceColumn ? [{ heading: 'Source', width: '230px', sortKey: 'source' }] : []),
    // Assign unique sortKeys for Debit and Credit
    { heading: 'Debit', width: '80px', align: 'right', sortKey: 'debitInCents' },
    { heading: 'Credit', width: '80px', align: 'right', sortKey: 'creditInCents' },
    { heading: 'Contractor', width: 'auto' },
    { heading: 'Sources', width: 'auto' },
    { heading: 'Notes', width: 'auto', maxWidth: '300px' },
    {
      heading: 'Has client comments, client categorization response, or internal notes',
      icon: 'speechBubble',
    },
    {
      heading: 'Categorization Method',
      icon: 'categorization',
      width: '32px',
    },
    { heading: 'Has attachment', icon: 'paperclip' },
    { heading: 'Has flagged internal note(s)', icon: 'flagFilled' },
    ...(showTasksColumn ? [{ heading: 'Client Tasks', icon: 'tasks' }] : []),
    { heading: 'Add-On', width: '100px' },
  ];

  columnHeadersCache.set(cacheKey, headers);
  return headers;
};

export const getItemKey = (index: number, data: Array<GeneralLedgerListItem>) => data[index].key;

const isSearchStringSet = search => typeof search === 'string' && search.trim() !== '';

// Helper function for string normalization (used by ledger account search)
const normalizeString = str => String(str).trim().toLowerCase();

// FIXME Extract this function to bk-web-app/search-utils/doesLedgerAccountContainSearchString in IN-2011
const ledgerAccountContainsSearchString = search => {
  const searchString = normalizeString(search);
  return ({ details, internalNotes }) => {
    if (normalizeString(details).includes(searchString)) {
      return true;
    }
    if (internalNotes.some(({ body }) => normalizeString(body).includes(searchString))) {
      return true;
    }
    // All the searches above failed to locate the search string
    return false;
  };
};

const shouldLedgerBeShown = (ledgerAccount, showAccounts) => {
  // When accounts are not shown if a ledger has a role (meaning it's an account) then don't show it
  if (!showAccounts && isAccount(ledgerAccount)) {
    return false;
  }
  const hasDataEntries = ledgerAccount.dataEntries.length > 0;
  // Always show the account if it has data entries
  if (hasDataEntries) {
    return true;
  }
  const hasOpeningBalance = typeof ledgerAccount.openingBalanceInCents === 'number';
  const hasClosingBalance = typeof ledgerAccount.closingBalanceInCents === 'number';
  // If there are no balances available to show, don't bother showing it
  if (!hasOpeningBalance && !hasClosingBalance) {
    return false;
  }
  const openingBalanceIsNonZero = ledgerAccount.openingBalanceInCents !== 0;
  const closingBalanceIsNonZero = ledgerAccount.closingBalanceInCents !== 0;
  const hasNonZeroBalance =
    (hasOpeningBalance && openingBalanceIsNonZero) ||
    (hasClosingBalance && closingBalanceIsNonZero);
  // If there are balances and one of them is not zero, then show the ledger
  if (hasNonZeroBalance) {
    return true;
  }
  // If there's no reason to show the ledger, then don't show it
  return false;
};

/**
 * Resolves the value of a nested property within an object based on a dot-separated path string.
 * If any part of the path is invalid or the property doesn't exist, it returns undefined.
 */
const resolveProperty = (obj: DataEntry, path: string) => {
  try {
    return path.split('.').reduce((acc, part) => {
      if (acc && typeof acc === 'object' && part in acc) {
        return acc[part];
      }
      return undefined;
    }, obj);
  } catch (error) {
    return undefined;
  }
};

const getOneLedgerAccountItems =
  ({
    dateRange,
    search,
    selectedDataEntry,
    toggleDataEntry,
    toggleLedgerAccount,
    client,
    potentialMatches,
    moneyInTransitLedgerId,
    sortStates,
    updateSortState,
    filterMito,
    updateFilterMito,
    showSourceColumn,
    showTasksColumn = false,
    taskStatusFilter,
    matchingEntryIds,
  }: MakeGeneralLedgerListItemDataArgs & {
    potentialMatches: PotentialMatchesResult;
  }) =>
  (ledgerAccount): Array<GeneralLedgerListItem> => {
    let shownDataEntries = ledgerAccount.dataEntries;
    const isMoneyInTransit = ledgerAccount.code === ACCOUNT_CODES.MONEY_IN_TRANSIT;
    const isMoneyInTransitOutstanding =
      ledgerAccount.code === ACCOUNT_CODES.MONEY_IN_TRANSIT_OUTSTANDING;
    const isPersonalBusinessAccount =
      ledgerAccount.category?.id === ACCOUNT_CATEGORY_IDS.PERSONAL_BUSINESS;

    const { debitTransactions, creditTransactions } = potentialMatches;
    const lookupMatchingAmountTransactions = (amount: number) => {
      const targetMap = amount > 0 ? creditTransactions : debitTransactions;
      return targetMap.get(-amount) || [];
    };

    const sortState = sortStates[ledgerAccount.id] || { key: '', direction: 'asc' };

    // Apply sorting
    shownDataEntries = applySorting(shownDataEntries, sortState);

    if (isSearchStringSet(search) && matchingEntryIds) {
      // Filter using pre-computed search results from the hook
      shownDataEntries = shownDataEntries.filter(entry => matchingEntryIds.has(entry.id));
      // While the user is searching, if the search filter results in all the data entries being
      // hidden, then hide the whole ledger unless the search string appears in the ledger itself.
      // This has to be done in this conditional block because a ledger could still be shown if it has
      // no data entries but has an opening or closing balance.
      if (
        shownDataEntries.length === 0 &&
        !ledgerAccountContainsSearchString(search)(ledgerAccount)
      ) {
        return [];
      }
    }

    // Filter entries based on task status filter
    if (taskStatusFilter && taskStatusFilter !== 'none') {
      if (taskStatusFilter === 'all') {
        // Show only entries with any task
        shownDataEntries = shownDataEntries.filter(entry => !!entry.task);
      } else if (taskStatusFilter === 'pending') {
        // Show only entries with pending tasks
        shownDataEntries = shownDataEntries.filter(
          entry => entry.task && entry.task.status === 'PENDING_WORK',
        );
      } else if (taskStatusFilter === 'confirmed') {
        // Show only entries with confirmed tasks (RESOLVED with CATEGORY_CONFIRMED)
        shownDataEntries = shownDataEntries.filter(
          entry => entry.task && entry.task.status === 'RESOLVED',
        );
      }
      // If filter results in no matching entries, hide the whole ledger
      if (shownDataEntries.length === 0) {
        return [];
      }
    }

    const potentialMatchHeaderColumn = getPotentialMatchHeaderColumn(isMoneyInTransitOutstanding);

    // Pre-compute values that are constant for all data entries in this ledger account
    const isAccountValue = isAccount(ledgerAccount);
    const shouldComputeMatches = !isMoneyInTransit && !isPersonalBusinessAccount;

    // Pre-compute common props to avoid object creation in map
    const commonItemProps = {
      search,
      filterMito,
      isMoneyInTransitOutstanding,
      toggleDataEntry,
      client,
      moneyInTransitLedgerId,
      showSourceColumn,
      showTasksColumn,
      isAccount: isAccountValue,
    };

    return [
      {
        key: `ledgerAccount[${ledgerAccount.id}]`,
        ItemComponent: LedgerAccountHeader,
        itemProps: {
          dateRange,
          ledgerAccount,
          search,
          toggleLedgerAccount,
          shownDataEntries,
        },
        NavComponent: LedgerNavigationLink,
        navProps: {
          children: ledgerAccount.name,
        },
      },
      {
        key: `ledgerAccount[${ledgerAccount.id}].dataEntryHeaderRow`,
        ItemComponent: DataEntryHeaderRow,
        itemProps: {
          columns: [
            potentialMatchHeaderColumn,
            ...getGeneralLedgerColumnHeaders(showSourceColumn, showTasksColumn),
          ],
          dataEntriesUnderHeader: shownDataEntries,
          ledgerAccount,
          client,
          sortState,
          onSortChange: (key, direction) => updateSortState(ledgerAccount.id, { key, direction }),
          filterMito,
          onMitoFilterChange: () => updateFilterMito(),
        },
      },
      ...shownDataEntries.map(dataEntry => {
        const isSelected = !!selectedDataEntry && selectedDataEntry.id === dataEntry.id;
        const matchingAmountTransactions = shouldComputeMatches
          ? lookupMatchingAmountTransactions(dataEntry.amountInCents)
          : [];

        return {
          key: `dataEntry[${dataEntry.id}]`,
          ItemComponent: DataEntryRow,
          itemProps: {
            ...commonItemProps,
            dataEntry,
            isSelected,
            matchingAmountTransactions,
          },
        };
      }),
      // Add space below the table so that it looks nice
      {
        key: `ledgerAccount[${ledgerAccount.id}].tableSpacer`,
        ItemComponent: SpacerListItem,
        itemProps: { height: 56 },
      },
    ];
  };

const getOneLedgerCategoryItems =
  (
    args: MakeGeneralLedgerListItemDataArgs & {
      potentialMatches: PotentialMatchesResult;
    },
  ) =>
  (ledgerAccountCategory): Array<GeneralLedgerListItem> => {
    const { showAccounts } = args;

    const shownLedgerAccounts = ledgerAccountCategory.ledgerAccounts.filter(ledgerAccount =>
      shouldLedgerBeShown(ledgerAccount, showAccounts),
    );

    if (shownLedgerAccounts.length === 0) {
      return [];
    }

    const ledgerAccountItems = flatten(shownLedgerAccounts.map(getOneLedgerAccountItems(args)));

    if (ledgerAccountItems.length === 0) {
      return [];
    }

    return [
      // Add the ledger account category header above the ledger accounts and their tables
      {
        key: `ledgerAccountCategory[${ledgerAccountCategory.id}]`,
        ItemComponent: CategoryHeader,
        itemProps: {
          text: ledgerAccountCategory.name,
        },
        NavComponent: LedgerNavigationHeader,
        navProps: {
          children: ledgerAccountCategory.name,
        },
      },
      // Then add all the ledger account items
      ...ledgerAccountItems,
    ];
  };

const getQualityIssueSummaryItems = ({
  dataEntries,
  hasQualityIssues,
  search,
  selectedDataEntry,
  showQualityIssues,
  showSourceColumn,
  showTasksColumn = false,
  toggleDataEntry,
  client,
  sortStates,
  updateSortState,
  matchingEntryIds,
}: MakeGeneralLedgerListItemDataArgs): Array<GeneralLedgerListItem> => {
  if (!showQualityIssues || !hasQualityIssues) {
    return [];
  }

  let dataEntriesWithQualityIssues = dataEntries.filter(
    ({ hasPotentialQualityIssues }) => hasPotentialQualityIssues,
  );

  if (isSearchStringSet(search) && matchingEntryIds) {
    // Filter using pre-computed search results from the hook
    dataEntriesWithQualityIssues = dataEntriesWithQualityIssues.filter(entry =>
      matchingEntryIds.has(entry.id),
    );
  }

  const sortState = sortStates['qualityIssues'] || { key: '', direction: 'asc' };
  // Apply sorting
  dataEntriesWithQualityIssues = applySorting(dataEntriesWithQualityIssues, sortState);

  if (dataEntriesWithQualityIssues.length === 0) {
    return [];
  }

  return [
    {
      key: 'qualityIssues.heading',
      ItemComponent: CategoryHeader,
      itemProps: {
        text: 'Quality Issues Summary',
      },
      NavComponent: LedgerNavigationHeader,
      navProps: {
        children: 'Quality Issues Summary',
      },
    },
    {
      key: 'qualityIssues.headingSpacer',
      ItemComponent: SpacerListItem,
      itemProps: { height: 16 },
    },
    {
      key: `qualityIssues.dataEntryHeaderRow`,
      ItemComponent: DataEntryHeaderRow,
      itemProps: {
        dataEntriesUnderHeader: dataEntriesWithQualityIssues,
        columns: getGeneralLedgerColumnHeaders(showSourceColumn, showTasksColumn),
        client,
        sortState, // Pass sortState to the header row
        onSortChange: (key, direction) => updateSortState('qualityIssues', { key, direction }),
      },
    },
    ...dataEntriesWithQualityIssues.map(dataEntry => {
      // Pre-compute stable values to reduce object creation
      const isSelected = !!selectedDataEntry && selectedDataEntry.id === dataEntry.id;
      return {
        key: `qualityIssues.dataEntry[${dataEntry.id}]`,
        ItemComponent: DataEntryRow,
        itemProps: {
          dataEntry,
          isSelected,
          search,
          showLedgerAccountName: true,
          toggleDataEntry,
          showSourceColumn,
          showTasksColumn,
        },
      };
    }),
    // Space below the table so that it looks nice
    {
      key: 'qualityIssues.tableSpacer',
      ItemComponent: SpacerListItem,
      itemProps: { height: 56 },
    },
  ];
};

const getNoDataItems = (): Array<GeneralLedgerListItem> => [
  {
    key: 'noData.heading',
    ItemComponent: CategoryHeader,
    itemProps: {
      text: 'Nothing found',
    },
    NavComponent: LedgerNavigationHeader,
    navProps: {
      children: 'Nothing found',
    },
  },
  {
    key: 'noData.message',
    ItemComponent: NoDataMessage,
    itemProps: {},
  },
];

type PotentialMatchesResult = {
  creditTransactions: Map<number, DataEntry[]>;
  debitTransactions: Map<number, DataEntry[]>;
};

// Creates a list of objects that represent the data shown in the main general ledger content area
// as well as the navigation sidebar. They are combined so that the same list can be used to render
// both content areas. This keeps them in sync: when sections are added and removed from the main
// content, the are updated in the navigation automatically.
const makeGeneralLedgerListItemData = (
  args: MakeGeneralLedgerListItemDataArgs,
): Array<GeneralLedgerListItem> => {
  const qualityIssueSummaryItems = getQualityIssueSummaryItems(args);

  // Use pre-computed potential matches or default to empty maps
  const potentialMatches = args.potentialMatches || {
    creditTransactions: new Map(),
    debitTransactions: new Map(),
  };

  const allLedgerCategoryItems = flatten(
    args.ledgerAccountCategories.map(
      getOneLedgerCategoryItems({
        ...args,
        potentialMatches,
      }),
    ),
  );
  let listItems = [...qualityIssueSummaryItems, ...allLedgerCategoryItems];
  listItems = listItems.length === 0 ? getNoDataItems() : listItems;
  // Augment the props for each item with its index in the list so that filtered lists still know
  // where they go in the page as a whole
  listItems.forEach(({ itemProps, navProps }, index) => {
    // eslint-disable-next-line no-param-reassign
    itemProps.index = index;
    if (navProps) {
      // eslint-disable-next-line no-param-reassign
      navProps.index = index;
    }
  });
  return listItems;
};

const applySorting = (
  dataEntries: DataEntry[],
  sortState: { key: string; direction: 'asc' | 'desc' },
): DataEntry[] => {
  if (!sortState.key) return dataEntries;

  // Map custom sortKeys to actual data properties
  const getSortValue = (entry: DataEntry) => {
    if (sortState.key === 'debitInCents') {
      // Debit: positive amounts only, otherwise treat as 0 for sorting
      return entry.amountInCents > 0 ? entry.amountInCents : 0;
    }
    if (sortState.key === 'creditInCents') {
      // Credit: negative amounts only, otherwise treat as 0 for sorting
      return entry.amountInCents < 0 ? Math.abs(entry.amountInCents) : 0;
    }
    if (sortState.key === 'source') {
      // Source: use formatSourceInfo to get the sortable string
      return formatSourceInfo(entry).toLowerCase();
    }
    // Fallback to generic property resolver
    return resolveProperty(entry, sortState.key) || '';
  };

  return [...dataEntries].sort((a, b) => {
    const aValue = getSortValue(a);
    const bValue = getSortValue(b);
    if (aValue < bValue) return sortState.direction === 'asc' ? -1 : 1;
    if (aValue > bValue) return sortState.direction === 'asc' ? 1 : -1;
    return 0;
  });
};

export default makeGeneralLedgerListItemData;
